import ExifReader from 'exifreader';
import { Coordinates } from '../types';

/**
 * Hvor mye av filen vi leser i første forsøk.
 *
 * EXIF ligger i APP1-segmentet rett etter SOI i en JPEG, altså helt i starten. 256 kB
 * dekker med god margin alle telefonkameraer — inkludert innebygde miniatyrer, som er
 * det som gjør APP1 stor i det hele tatt.
 */
export const EXIF_HEAD_BYTES = 256 * 1024;

/**
 * Les GPS-koordinater fra et bilde uten å dra hele filen inn i minnet.
 *
 * HVORFOR: kallstedet gjorde `await file.arrayBuffer()` på hele filen før parsing.
 * Et moderne telefonbilde er 10–50 MB, og på Android ble det lest inn i JS-heapen
 * for hvert bilde brukeren la til — rett før tre andre fulldekodinger av samme fil.
 * Del av treghets-rapporten 23.08.2026.
 *
 * Strategi: prøv de første `EXIF_HEAD_BYTES` først. Finner vi GPS der, er vi ferdige
 * uten å ha rørt resten av filen. Formater som legger metadata et annet sted (HEIC
 * kan ha `meta`-boksen etter bildedataene) faller tilbake til hele filen, så vi
 * aldri MISTER en koordinat vi før fant — vi slutter bare å betale for den i det
 * vanlige tilfellet.
 *
 * Returnerer null når bildet ikke har GPS, eller ikke lot seg parse.
 */
export async function readExifGps(file: File | Blob): Promise<Coordinates | null> {
  const fromHead = await tryParse(file.slice(0, EXIF_HEAD_BYTES));
  if (fromHead) return fromHead;
  // Hodet holdt ikke (metadata lenger inn, eller avkuttet segment) — ta hele filen.
  if (file.size > EXIF_HEAD_BYTES) return await tryParse(file);
  return null;
}

async function tryParse(blob: Blob): Promise<Coordinates | null> {
  try {
    const tags = await ExifReader.load(await blob.arrayBuffer(), { expanded: true });
    const latitude = tags.gps?.Latitude;
    const longitude = tags.gps?.Longitude;
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return { latitude, longitude };
    }
  } catch {
    // Avkuttet eller ukjent format — kalleren avgjør om den vil prøve hele filen.
  }
  return null;
}

/**
 * Avstand i meter mellom to punkter, flat approksimasjon. God nok for «er bildet
 * tatt langt fra plassen?» på noen hundre meter, som er det eneste bruket.
 */
export function approxDistanceMeters(a: Coordinates, b: Coordinates): number {
  const dLat = (a.latitude - b.latitude) * 111320;
  const dLon = (a.longitude - b.longitude) * 111320 * Math.cos(b.latitude * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon);
}
